import React, { useContext, useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Fade, Grid, Paper } from '@mui/material';
import * as yup from 'yup';
import { postData, getData, putData } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { CustomForm } from '../../components/formSections';
import { GlobalsContext } from '../../context/globals';
import { useSnackbar } from 'notistack';

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

function Page() {
    const { classes } = useStyles();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState(false);
    const [organismos, setOrganismos] = useState([]);
    const [tiposArea, setTiposArea] = useState([]);

    const loadCatalogos = useCallback(async () => {
        const [respOrg, respTipo] = await Promise.all([
            getData('catalogos/organismos', usuario.token),
            getData('catalogos/tipos-area', usuario.token),
        ]);
        if (respOrg.error === '') setOrganismos(respOrg.data || []);
        if (respTipo.error === '') setTiposArea(respTipo.data || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario.token]);

    const loadData = useCallback(async () => {
        if (globalsContext.idEdit) {
            loadingModal.setOpen(true);
            try {
                const response = await getData(`areas/${globalsContext.idEdit}`, usuario.token);
                if (response.error === '') setData(response.data);
                else enqueueSnackbar(response.error, { variant: 'error' });
            } finally {
                loadingModal.setOpen(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalsContext.idEdit, usuario.token]);

    useEffect(() => { loadCatalogos(); }, [loadCatalogos]);
    useEffect(() => { if (globalsContext.idEdit) loadData(); }, [globalsContext.idEdit, loadData]);

    const onClose = useCallback(() => navigate(-1), [navigate]);
    const onCancel = () => alertModal.showModal('Cancelar', 'Se perderán los cambios. ¿Continuar?', onClose);

    const onSubmit = async (values) => {
        loadingModal.setOpen(true);
        try {
            const areaData = {
                id_organismo: Number(values.id_organismo),
                id_tipo_area: values.id_tipo_area ? Number(values.id_tipo_area) : null,
                cuof: values.cuof || null,
                nombre: values.nombre,
            };
            let response;
            if (data && data.id_area) {
                response = await putData(`areas/${data.id_area}`, areaData, usuario.token);
            } else {
                response = await postData('areas', areaData, usuario.token);
            }
            if (response.error === '') {
                enqueueSnackbar(data ? 'Área modificada' : 'Área creada', { variant: 'success' });
                onClose();
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const formSections = useMemo(() => [
        {
            name: 'Datos del área',
            fields: [
                {
                    dataName: 'nombre', label: 'Nombre del área', controlType: 'textField',
                    initialValue: data ? data.nombre : '',
                    validationSchema: yup.string().required('El nombre es requerido'),
                    autoFocus: true,
                },
                {
                    dataName: 'cuof', label: 'CUOF (Código Único de Oficina)', controlType: 'textField',
                    initialValue: data ? (data.cuof || '') : '',
                    validationSchema: yup.string().nullable().max(20, 'Máximo 20 caracteres'),
                },
                {
                    dataName: 'id_organismo', label: 'Organismo', controlType: 'select',
                    items: organismos,
                    initialValue: data ? String(data.id_organismo || '') : '',
                    validationSchema: yup.number().required('El organismo es requerido'),
                },
                {
                    dataName: 'id_tipo_area', label: 'Tipo de área', controlType: 'select',
                    items: tiposArea,
                    initialValue: data ? String(data.id_tipo_area || '') : '',
                    validationSchema: yup.number().nullable(),
                },
            ],
        },
    ], [data, organismos, tiposArea]);

    return (
        <Fragment>
            <Layout nombrePagina={data ? 'Modificar Área' : 'Nueva Área'}>
                <Fade in={true} timeout={900}>
                    <Grid size={12}>
                        <Paper className={classes.paper}>
                            <CustomForm
                                formSections={formSections}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={data ? 'Guardar cambios' : 'Crear área'}
                                cancelButtonText="Cancelar"
                                stepperType={false}
                                isModal={false}
                            />
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>
        </Fragment>
    );
}

export default Page;
